import React, { useState, useEffect, useCallback } from 'react';
import ErpTemplate from '../../components/ErpTemplate';
import api from '../../services/api';
import { data } from '../../util/util';

export default function ResumoRsvp({ match }) {
    const eventoId = match.params.id;
    const token = localStorage.getItem('token');
    const [dados, setDados] = useState({});
    const [loading, setLoading] = useState(true);

    const buscarDados = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api({
                method: 'get',
                action: '/evento/' + eventoId,
                token: token,
            });

            if (response.data && response.data.data) {
                const dadosRecebidos = response.data.data;

                // Processar dados do RSVP
                const rsvpData = dadosRecebidos.evento_rsvp_data || dadosRecebidos.eventoRsvpData || {};

                // Converter data de previsão de recebimento das listas
                if (rsvpData.previsao_recebimento_listas) {
                    rsvpData.previsao_recebimento_listas = data(rsvpData.previsao_recebimento_listas);
                }

                // Processar dados do Backoffice (Abertura) se existir
                // A API retorna a relação `backoffice` como objeto com `dados_backoffice` (JSON)
                const boWrapper = dadosRecebidos.backoffice || {};
                const boData = boWrapper.dados_backoffice || boWrapper.dadosBackoffice || {};
                // Arquivo: "Verificação da WBS" (no backend é verificacao_oi_arquivo)
                if (boWrapper.verificacao_oi_arquivo || boWrapper.verificacaoOiArquivo) {
                    boData.verificacaoOiArquivoObj = boWrapper.verificacao_oi_arquivo || boWrapper.verificacaoOiArquivo;
                }

                setDados({
                    ...dadosRecebidos,
                    backoffice: boData,
                    evento_rsvp_data: rsvpData
                });
            }
        } catch (error) {
            console.error('Erro ao buscar dados:', error);
        } finally {
            setLoading(false);
        }
    }, [eventoId, token]);

    useEffect(() => {
        buscarDados();
    }, [buscarDados]);

    if (loading) {
        return (
            <ErpTemplate>
                <div className="main-content container-fluid">
                    <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                        <p className="mt-2">Carregando dados...</p>
                    </div>
                </div>
            </ErpTemplate>
        );
    }

    const rsvpData = dados.evento_rsvp_data || {};
    const rsvpAtivado = (dados.rsvp ?? 'nao') === 'sim';
    const isSanofi = (dados.modeloCliente ?? 'master') === 'sanofi';
    const backoffice = dados.backoffice || {};

    const formatBRL = (valor) => {
        if (valor === null || valor === undefined || valor === '') return '-';
        const n = typeof valor === 'string' ? Number(valor) : valor;
        if (Number.isNaN(n)) return String(valor);
        return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const tiposEventoUnico = [
        { key: 'exclusivo', label: 'Exclusivo' },
        { key: 'tipo1', label: 'Evento de Terceiro - Tipo 1' },
        { key: 'tipo2', label: 'Evento de Terceiro - Tipo 2' },
        { key: 'simposio', label: 'Evento de Terceiro - Simpósio' },
        { key: 'advisoryBoard', label: 'Advisory Board' },
        { key: 'consultingService', label: 'Consulting Service' },
        { key: 'scientificEducationalProgramMeeting', label: 'Scientific Educational Program/Meeting' },
        { key: 'speakerMeetingOneTime', label: 'Speaker Meeting (One-Time)' },
        { key: 'speakerProgramSeries', label: 'Speaker Program (Series)' },
        { key: 'speakerTraining', label: 'Speaker Training' },
        { key: 'reuniaoEquipeHospitalarClinica', label: 'Reunião com Equipe Hospitalar e Clínica' },
    ];

    const combosPrevisao = [
        { key: 'eventoRaizTipo1Tipo2', label: 'Evento Raiz Tipo1 e Evento Tipo 2' },
        { key: 'eventoRaizTipo1Simposio', label: 'Evento Raiz Tipo1 e Simpósio' },
        { key: 'eventoRaizTipo1Tipo2Simposio', label: 'Evento Raiz Tipo1; Evento Tipo 2 e Simpósio' },
    ];

    const eventoUnicoAtivo = backoffice?.eventoUnico === true;
    const tiposSelecionados = tiposEventoUnico.filter(t => backoffice?.[t.key]).map(t => t.label);

    return (
        <ErpTemplate>
            <div className="main-content container-fluid">
                <div className="page-title">
                    <h3>Resumo RSVP - {dados.nome || ''}</h3>
                </div>

                <div className="row">
                    <div className="col-sm-12">
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="mb-0" style={{ padding: '8px' }}>💳 WBS (Financeiro)</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-sm-4 mb-2">
                                        <label className="form-label"><strong>Número WBS</strong></label>
                                        <p className="form-control-plaintext">{dados.numeroOi || '-'}</p>
                                    </div>
                                    <div className="col-sm-4 mb-2">
                                        <label className="form-label"><strong>Valor WBS (R$)</strong></label>
                                        <p className="form-control-plaintext">{formatBRL(dados.valorOi)}</p>
                                    </div>
                                    <div className="col-sm-4 mb-2">
                                        <label className="form-label"><strong>Saldo WBS (R$)</strong></label>
                                        <p className="form-control-plaintext">{formatBRL(dados.saldoOi)}</p>
                                    </div>
                                </div>

                                <div className="row mt-2">
                                    <div className="col-sm-12">
                                        <label className="form-label"><strong>Arquivo “Verificação da WBS”</strong></label>
                                        {backoffice?.verificacaoOiArquivoObj?.id ? (
                                            <p className="form-control-plaintext">
                                                <a
                                                    href={`${process.env.REACT_APP_API_URL}/arquivo/download/${backoffice.verificacaoOiArquivoObj.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary"
                                                >
                                                    {backoffice.verificacaoOiArquivoObj.nome?.split('\\').pop().split('/').pop() || 'Baixar arquivo'}
                                                </a>
                                            </p>
                                        ) : (
                                            <p className="form-control-plaintext text-muted">-</p>
                                        )}
                                    </div>
                                </div>
                                {rsvpAtivado && (
                                    <div className="row mb-3">
                                        <div className="col-sm-12">
                                            <label className="form-label"><strong>Malha Aérea</strong></label>
                                            {rsvpData.malha_aerea_arquivo?.id ? (
                                                <p className="form-control-plaintext">
                                                    <a
                                                        href={`${process.env.REACT_APP_API_URL}/arquivo/download/${rsvpData.malha_aerea_arquivo.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary"
                                                    >
                                                        {rsvpData.malha_aerea_arquivo.nome?.split('\\').pop().split('/').pop() || 'Baixar Malha Aérea'}
                                                    </a>
                                                </p>
                                            ) : (
                                                <p className="form-control-plaintext text-muted">N/A</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-12">
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="mb-0" style={{ padding: '8px' }}>📌 Previsão de Tipos de Eventos (BackOffice Abertura)</h5>
                            </div>
                            <div className="card-body">
                                <div className="row mb-2">
                                    <div className="col-sm-12">
                                        <label className="form-label"><strong>Evento único</strong></label>
                                        <p className="form-control-plaintext">{eventoUnicoAtivo ? 'Sim' : 'Não'}</p>
                                    </div>
                                </div>

                                <div className="row mb-3">
                                    <div className="col-sm-12">
                                        <label className="form-label"><strong>Tipos selecionados</strong></label>
                                        {eventoUnicoAtivo ? (
                                            tiposSelecionados.length > 0 ? (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                    {tiposSelecionados.map((t) => (
                                                        <span key={t} className="badge bg-secondary">{t}</span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="form-control-plaintext text-muted">-</p>
                                            )
                                        ) : (
                                            <p className="form-control-plaintext text-muted">-</p>
                                        )}
                                    </div>
                                </div>

                                <div className="row">
                                    {combosPrevisao.map((c) => (
                                        <div className="col-sm-4 mb-2" key={c.key}>
                                            <label className="form-label"><strong>{c.label}</strong></label>
                                            <p className="form-control-plaintext">{backoffice?.[c.key] ? 'Sim' : 'Não'}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Primeira linha: Configurações de RSVP + Dados do RSVP */}
                <div className="row">
                    <div className="col-sm-6">
                        {/* Configurações de RSVP */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="mb-0" style={{ padding: '8px' }}>📋 Configurações de RSVP</h5>
                            </div>
                            <div className="card-body">
                                <div className="row mb-3">
                                    <div className="col-sm-12">
                                        <label className="form-label"><strong>RSVP Ativado</strong></label>
                                        <p className="form-control-plaintext">{rsvpAtivado ? 'Sim' : 'Não'}</p>
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-sm-12">
                                        <label className="form-label"><strong>Responsável por Subir Participantes no Sistema do cliente</strong></label>
                                        <p className="form-control-plaintext">
                                            {rsvpData.responsavel_subir_participantes === 'backoffice' ? 'BackOffice' :
                                                rsvpData.responsavel_subir_participantes === 'responsavel_evento' ? 'Responsável do evento' :
                                                    rsvpData.responsavel_subir_participantes === 'cliente' ? 'Cliente' : '-'}
                                        </p>
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-sm-12">
                                        <label className="form-label"><strong>Planilha de Convidados</strong></label>
                                        {rsvpData.planilha_arquivo?.id ? (
                                            <p className="form-control-plaintext">
                                                <a
                                                    href={`${process.env.REACT_APP_API_URL}/arquivo/download/${rsvpData.planilha_arquivo.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary"
                                                >
                                                    {rsvpData.planilha_arquivo.nome?.split('\\').pop().split('/').pop() || 'Baixar Planilha'}
                                                </a>
                                            </p>
                                        ) : (
                                            <p className="form-control-plaintext text-muted">N/A</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-sm-6">
                        {/* Dados do RSVP */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="mb-0" style={{ padding: '8px' }}>📊 Dados do RSVP</h5>
                            </div>
                            <div className="card-body">
                                <div className="row mb-3">
                                    <div className="col-sm-12">
                                        <label className="form-label"><strong>Link de Inscrição</strong></label>
                                        <p className="form-control-plaintext">
                                            {rsvpData.link_inscricao ? (
                                                <a href={rsvpData.link_inscricao} target="_blank" rel="noopener noreferrer">{rsvpData.link_inscricao}</a>
                                            ) : '-'}
                                        </p>
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-sm-6">
                                        <label className="form-label"><strong>Total de Participantes Esperados</strong></label>
                                        <p className="form-control-plaintext">{dados.quantidadePessoas || '-'}</p>
                                    </div>
                                    <div className="col-sm-6">
                                        <label className="form-label"><strong>Quantidade de HCP</strong></label>
                                        <p className="form-control-plaintext">{rsvpData.quantidade_hcp || '-'}</p>
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-sm-12">
                                        <label className="form-label"><strong>Speakers Confirmados</strong></label>
                                        <p className="form-control-plaintext">{rsvpData.speakers_confirmados || '-'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <>
                    {/* Segunda linha: Pacotes + Informações Adicionais */}
                    <div className="row">
                        <div className="col-sm-6">
                            {/* Pacotes */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="mb-0" style={{ padding: '8px' }}>🎁 Pacotes</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-3">
                                        <div className="col-sm-6">
                                            <label className="form-label"><strong>Pacote Aéreo</strong></label>
                                            <p className="form-control-plaintext">
                                                {rsvpData.pacote_aereo_quantidade !== null && rsvpData.pacote_aereo_quantidade !== undefined
                                                    ? `Sim (Quantidade: ${rsvpData.pacote_aereo_quantidade})`
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                        <div className="col-sm-6">
                                            <label className="form-label"><strong>Classe de Voo</strong></label>
                                            <p className="form-control-plaintext">
                                                {rsvpData.classevoo?.nome ?? (rsvpData.classevoo ? String(rsvpData.classevoo) : '-')}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-6">
                                            <label className="form-label"><strong>Pacote Hospedagem</strong></label>
                                            <p className="form-control-plaintext">
                                                {rsvpData.pacote_hospedagem_quantidade !== null && rsvpData.pacote_hospedagem_quantidade !== undefined
                                                    ? `Sim (Quantidade: ${rsvpData.pacote_hospedagem_quantidade})`
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-6">
                                            <label className="form-label"><strong>Pacote Transfer</strong></label>
                                            <p className="form-control-plaintext">
                                                {rsvpData.pacote_transfer_quantidade !== null && rsvpData.pacote_transfer_quantidade !== undefined
                                                    ? `Sim (Quantidade: ${rsvpData.pacote_transfer_quantidade})`
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-6">
                                            <label className="form-label"><strong>Pacote Inscrição</strong></label>
                                            <p className="form-control-plaintext">
                                                {rsvpData.numero_inscricoes !== null && rsvpData.numero_inscricoes !== undefined
                                                    ? `Sim (Quantidade: ${rsvpData.numero_inscricoes})`
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                        <div className="col-sm-6">
                                            <label className="form-label"><strong>Pacote Completo</strong></label>
                                            <p className="form-control-plaintext">
                                                {rsvpData.pacote_completo_quantidade !== null && rsvpData.pacote_completo_quantidade !== undefined
                                                    ? `Sim (Quantidade: ${rsvpData.pacote_completo_quantidade})`
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-6">
                            {/* Informações Adicionais */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="mb-0" style={{ padding: '8px' }}>ℹ️ Informações Adicionais</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-3">
                                        <div className="col-sm-12">
                                            <label className="form-label"><strong>Prazo da Sociedade/Associação</strong></label>
                                            <p className="form-control-plaintext" style={{ whiteSpace: 'pre-wrap' }}>{rsvpData.link_quantidade_inscricao_prazo_sociedade || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-12">
                                            <label className="form-label"><strong>Valor Aprovado de Aéreo</strong></label>
                                            <p className="form-control-plaintext">{rsvpData.quantidade_prevista_aereo_valor_aprovado || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-12">
                                            <label className="form-label"><strong>Observações de Aéreo</strong></label>
                                            <p className="form-control-plaintext" style={{ whiteSpace: 'pre-wrap' }}>{rsvpData.observacoes_aereo || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-6">
                                            <label className="form-label"><strong>Considerar seguro viagem?</strong></label>
                                            <p className="form-control-plaintext">
                                                {rsvpData.seguro_viagem === 'sim' ? 'Sim' :
                                                    rsvpData.seguro_viagem === 'nao' ? 'Não' : '-'}
                                            </p>
                                        </div>
                                        <div className="col-sm-6">
                                            <label className="form-label"><strong>Bilhete com Reembolso</strong></label>
                                            <p className="form-control-plaintext">
                                                {rsvpData.reembolso_aereo === 'sim' ? 'Sim' :
                                                    rsvpData.reembolso_aereo === 'nao' ? 'Não' : '-'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-12">
                                            <label className="form-label"><strong>Informações de Transfer</strong></label>
                                            <p className="form-control-plaintext" style={{ whiteSpace: 'pre-wrap' }}>{rsvpData.informacoes_transfer || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-12">
                                            <label className="form-label"><strong>Informações de Inscrições</strong></label>
                                            <p className="form-control-plaintext" style={{ whiteSpace: 'pre-wrap' }}>{rsvpData.informacoes_inscricoes || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-6">
                                            <label className="form-label"><strong>Sistema já Aprovado?</strong></label>
                                            <p className="form-control-plaintext">
                                                {dados.sistema_aprovado === 'sim' ? 'Sim' :
                                                    dados.sistema_aprovado === 'nao' ? 'Não' : '-'}
                                            </p>
                                        </div>
                                        <div className="col-sm-6">
                                            <label className="form-label"><strong>Previsão de Recebimento das Listas</strong></label>
                                            <p className="form-control-plaintext">
                                                {rsvpData.previsao_recebimento_listas ? data(rsvpData.previsao_recebimento_listas) : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Terceira linha: Hotel e Hospedagem */}
                    <div className="row">
                        <div className="col-sm-6">
                            {/* Hotel e Hospedagem */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="mb-0" style={{ padding: '8px' }}>🏨 Hotel e Hospedagem</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-3">
                                        <div className="col-sm-6">
                                            <label className="form-label"><strong>Possui hotel?</strong></label>
                                            <p className="form-control-plaintext">
                                                {rsvpData.hotel === 'sim' ? 'Sim' :
                                                    rsvpData.hotel === 'nao' ? 'Não' : '-'}
                                            </p>
                                        </div>
                                    </div>
                                    {rsvpData.lista_hoteis && Array.isArray(rsvpData.lista_hoteis) && rsvpData.lista_hoteis.length > 0 ? (
                                        <div className="row mb-3">
                                            <div className="col-sm-12">
                                                <label className="form-label"><strong>Lista de hotéis</strong></label>
                                                <div className="table-responsive">
                                                    <table className="table table-sm table-bordered">
                                                        <thead>
                                                            <tr>
                                                                <th>Hotel</th>
                                                                <th>Data check-in</th>
                                                                <th>Hora check-in</th>
                                                                <th>Data check-out</th>
                                                                <th>Hora check-out</th>
                                                                <th>Quantidade de pessoas</th>
                                                                {rsvpData.lista_hoteis.some(h => 'bloqueados_single' in h) && <th>Bloqueados (Single)</th>}
                                                                {rsvpData.lista_hoteis.some(h => 'bloqueados_double' in h) && <th>Bloqueados (Double)</th>}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {rsvpData.lista_hoteis.map((item, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{item.nome_hotel || item.nomeHotel || '-'}</td>
                                                                    <td>{item.data_checkin ? data(item.data_checkin) : '-'}</td>
                                                                    <td>{item.hora_checkin || '-'}</td>
                                                                    <td>{item.data_checkout ? data(item.data_checkout) : '-'}</td>
                                                                    <td>{item.hora_checkout || '-'}</td>
                                                                    <td>{item.quantidade_pessoas != null && item.quantidade_pessoas !== '' ? item.quantidade_pessoas : '-'}</td>
                                                                    {rsvpData.lista_hoteis.some(h => 'bloqueados_single' in h) && <td>{item.bloqueados_single ?? '-'}</td>}
                                                                    {rsvpData.lista_hoteis.some(h => 'bloqueados_double' in h) && <td>{item.bloqueados_double ?? '-'}</td>}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    ) : rsvpData.hotel === 'sim' ? (
                                        <div className="row mb-3">
                                            <div className="col-sm-12">
                                                <label className="form-label"><strong>Lista de hotéis</strong></label>
                                                <p className="form-control-plaintext text-warning">A definir</p>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-6">
                            {/* Arquivos */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="mb-0" style={{ padding: '8px' }}>📎 Arquivos</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-3">
                                        <div className="col-sm-12">
                                            <label className="form-label"><strong>Agenda Prévia</strong></label>
                                            {rsvpData.agenda_previa_arquivo?.id ? (
                                                <p className="form-control-plaintext">
                                                    <a
                                                        href={`${process.env.REACT_APP_API_URL}/arquivo/download/${rsvpData.agenda_previa_arquivo.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary"
                                                    >
                                                        {rsvpData.agenda_previa_arquivo.nome?.split('\\').pop().split('/').pop() || 'Baixar Agenda Prévia'}
                                                    </a>
                                                </p>
                                            ) : rsvpData.agenda_previa_texto ? (
                                                <p className="form-control-plaintext" style={{ whiteSpace: 'pre-wrap' }}>{rsvpData.agenda_previa_texto}</p>
                                            ) : (
                                                <p className="form-control-plaintext text-muted">-</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-12">
                                            <label className="form-label"><strong>Documentos Diversos</strong></label>
                                            {rsvpData.documentos_diversos_arquivos && Array.isArray(rsvpData.documentos_diversos_arquivos) && rsvpData.documentos_diversos_arquivos.length > 0 ? (
                                                <div>
                                                    {rsvpData.documentos_diversos_arquivos.map((arquivo, index) => (
                                                        <p key={index} className="form-control-plaintext">
                                                            <a
                                                                href={`${process.env.REACT_APP_API_URL}/arquivo/download/${arquivo.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary"
                                                            >
                                                                {arquivo.nome?.split('\\').pop().split('/').pop() || `Documento ${index + 1}`}
                                                            </a>
                                                        </p>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="form-control-plaintext text-muted">-</p>
                                            )}
                                        </div>
                                    </div>
                                    {/* Seguro terrestre (D-0094): marcado na aba Geral do evento. O RSVP baixa a
                                        apólice daqui e encaminha ao convidado; a carta mostra o seguro só a quem
                                        tem transfer ou transfer extra ({se.seguroterrestre}). */}
                                    <div className="row mb-3">
                                        <div className="col-sm-12">
                                            <label className="form-label"><strong>Seguro Terrestre</strong></label>
                                            <p className="form-control-plaintext">
                                                {[true, 1, '1'].includes(dados.seguro_terrestre)
                                                    ? 'Sim — vale para quem tem transfer ou transfer extra'
                                                    : 'Não'}
                                            </p>
                                            {[true, 1, '1'].includes(dados.seguro_terrestre) && (
                                                (dados.arquivos_apolice_terrestre || []).length > 0 ? (
                                                    <div>
                                                        {dados.arquivos_apolice_terrestre.map((apolice, index) => (
                                                            <p key={apolice.id} className="form-control-plaintext">
                                                                <a
                                                                    href={`${process.env.REACT_APP_API_URL}/arquivo/download/${apolice.arquivo?.id ?? apolice.arquivo}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-primary"
                                                                >
                                                                    {apolice.arquivo?.nome?.split('\\').pop().split('/').pop() || `Apólice ${index + 1}`}
                                                                </a>
                                                            </p>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="form-control-plaintext text-muted">Apólice ainda não anexada</p>
                                                )
                                            )}
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-sm-12">
                                            <label className="form-label"><strong>E-mails para Cópia</strong></label>
                                            <p className="form-control-plaintext" style={{ whiteSpace: 'pre-wrap' }}>{rsvpData.emails_copia || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quarta linha: Controle de Cotas + Observações Gerais */}
                    <div className="row">
                        <div className="col-sm-6">
                            {/* Controle de Cotas */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="mb-0" style={{ padding: '8px' }}>🎯 Controle de Cotas</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-3">
                                        <div className="col-sm-12">
                                            <label className="form-label"><strong>Arquivo Controle de Cotas</strong></label>
                                            {rsvpData.arquivo_controle_cotas?.id ? (
                                                <p className="form-control-plaintext">
                                                    <a
                                                        href={`${process.env.REACT_APP_API_URL}/arquivo/download/${rsvpData.arquivo_controle_cotas.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary"
                                                    >
                                                        {rsvpData.arquivo_controle_cotas.nome?.split('\\').pop().split('/').pop() || 'Baixar Arquivo Controle de Cotas'}
                                                    </a>
                                                </p>
                                            ) : rsvpData.observacoes_controle_cotas === 'N/A' ? (
                                                <p className="form-control-plaintext text-muted">N/A</p>
                                            ) : (
                                                <p className="form-control-plaintext text-warning">A definir</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-6">
                            {/* Observações Gerais */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="mb-0" style={{ padding: '8px' }}>📝 Observações Gerais</h5>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-3">
                                        <div className="col-sm-12">
                                            <label className="form-label"><strong>Observações Gerais</strong></label>
                                            <p className="form-control-plaintext" style={{ whiteSpace: 'pre-wrap' }}>{rsvpData.observacoes_gerais || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>

                {!rsvpAtivado && (
                    <div className="alert alert-warning">
                        RSVP desativado para este evento.
                    </div>
                )}
            </div>
        </ErpTemplate>
    );
}

